import type { Learning, LearningsTarget } from "../types/LearningTypes";

/**
 * O que a tela mostra de cada proposta (AB#87286), puro para ser testado sem React.
 */

/** Teto do texto editado — o mesmo da api (`LEARNING_CONTENT_MAX`). */
export const LEARNING_CONTENT_MAX = 4000;

/** As chaves das consultas da caixa: o contador da aba atualiza junto com a lista. */
export function learningsQueryKey(target: LearningsTarget): string[] {
  return ["learnings", target.view, target.productId ?? "-"];
}

/** Chave i18n do destino: "Memória pessoal", "Memória do workspace", "Mind"... */
export function destinationKey(learning: Pick<Learning, "target" | "scope">): string {
  if (learning.target === "mind") return "learnings.destination.mind";
  return `learnings.destination.${learning.scope ?? "workspace"}`;
}

/** Chave i18n da ação sobre o destino: criar, atualizar, esquecer, unir. */
export function actionKey(learning: Pick<Learning, "target" | "action">): string {
  return `learnings.action.${learning.target}.${learning.action}`;
}

/**
 * Por quem a sugestão da Mind espera: "você", quando quem vê é admin; senão, um administrador.
 * Aprendizado da Memory não espera ninguém — ele já foi aplicado.
 */
export function awaitingKey(learning: Pick<Learning, "status" | "can_decide" | "target">): string | null {
  if (learning.status !== "pending" || learning.target !== "mind") return null
  return learning.can_decide ? "learnings.awaiting.you" : "learnings.awaiting.admins"
}

/** Dá para editar o texto antes de aceitar: tudo que grava um texto (esquecer não grava). */
export function isEditable(learning: Pick<Learning, "action" | "content">): boolean {
  return learning.action !== "delete" && learning.content !== null;
}

/** Mostrar antes/depois: Mind atualizada e consolidação de memórias. */
export function showsBefore(learning: Pick<Learning, "before_content" | "action">): boolean {
  return !!learning.before_content && learning.action !== "create";
}

export type LineChange = { kind: "same" | "added" | "removed"; text: string };

/**
 * Diferença por linha entre o antes e o depois (LCS): o bastante para quem aprova ver o
 * que a proposta muda num bloco da Mind, sem trazer um editor de diff para a lista.
 */
export function lineDiff(before: string, after: string): LineChange[] {
  const a = before.split("\n");
  const b = after.split("\n");
  const table: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const changes: LineChange[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      changes.push({ kind: "same", text: a[i] });
      i++;
      j++;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      changes.push({ kind: "removed", text: a[i++] });
    } else {
      changes.push({ kind: "added", text: b[j++] });
    }
  }
  while (i < a.length) changes.push({ kind: "removed", text: a[i++] });
  while (j < b.length) changes.push({ kind: "added", text: b[j++] });
  return changes;
}
